import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import {
  ACTION_LABELS,
  MATRIX_ACTIONS,
  MEMBER_STATUS_LABELS,
  RESOURCE_LABELS,
  RESOURCE_ORDER,
  type MemberStatus,
  type PermissionEntry,
  type RoleWithPermissions,
  type CompteBoutique,
  type StoreMember,
} from '@iobiz/shared'
import { useSessionStore } from '../stores/sessionStore.js'
import { VignetteFiche } from '../ui/VignetteFiche.js'
import { ListeComptes } from '../features/team/ListeComptes.js'
import {
  fetchComptes,
  fetchMembers,
  fetchRoles,
  saveRolePermissions,
} from '../features/team/api.js'

/**
 * W-30 · Paramètres — Utilisateurs & rôles.
 *
 * Deux onglets parce que ce sont deux gestes distincts : « qui travaille ici »
 * se consulte souvent, « que peut faire un rôle » se règle une fois puis
 * s'oublie. Les mêler dans un seul écran ferait payer à l'usage quotidien le
 * coût d'un réglage rare.
 */

const COULEUR_STATUT: Record<MemberStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  invited: 'warning',
  suspended: 'default',
}

function dateCourte(iso: string | null): string {
  if (!iso) return 'Jamais'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function TeamPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))
  const storeId = store?.storeId

  const [tab, setTab] = useState(0)
  const [membres, setMembres] = useState<StoreMember[] | null>(null)
  const [roles, setRoles] = useState<RoleWithPermissions[] | null>(null)
  const [permissions, setPermissions] = useState<PermissionEntry[]>([])
  const [comptes, setComptes] = useState<CompteBoutique[]>([])
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(async () => {
    if (!storeId) return
    setErreur(null)
    try {
      const [m, r, c] = await Promise.all([
        fetchMembers(storeId),
        fetchRoles(storeId),
        // ⚠️ Peut échouer sans droit « Paramètres » : l'écran reste lisible
        // sans la section Équipe plutôt que de ne rien afficher du tout.
        fetchComptes(storeId).catch(() => ({ data: [] as CompteBoutique[] })),
      ])
      setMembres(m.data)
      setRoles(r.roles)
      setPermissions(r.permissions)
      setComptes(c.data)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Chargement impossible.')
      setMembres([])
    }
  }, [storeId])

  useEffect(() => {
    void charger()
  }, [charger])

/**
   * Tout le monde SAUF les administrateurs de la plateforme.
   *
   * ⭐ Un administrateur de plateforme n'est pas quelqu'un de la boutique : il
   * y est rattaché pour l'exploiter, pas pour y travailler. Le faire figurer
   * parmi le personnel donne un effectif faux et une personne de plus à qui
   * l'on ne peut rien demander. L'administrateur de la BOUTIQUE, lui, reste
   * affiché — c'est un utilisateur de l'enseigne à part entière.
   *
   * ⚠️ Filtre d'AFFICHAGE seulement : aucune appartenance n'est touchée, les
   * accès sont identiques. Masquer et retirer sont deux gestes différents, et
   * les confondre ici couperait l'accès de l'exploitant à l'enseigne.
   */
  const membresAffiches = (membres ?? []).filter((m) => !m.isPlatformAdmin)

  if (!storeId || membres === null || roles === null) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 1 }}>
        Utilisateurs
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        {comptes.length + membresAffiches.length} personne
        {comptes.length + membresAffiches.length > 1 ? 's' : ''} dans{' '}
        {store?.name ?? 'la boutique'}
      </Typography>

      {erreur && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 4 }}>
        <Tab label="Personnes" />
        <Tab label="Rôles & permissions" />
      </Tabs>

      {tab === 0 && (
        <>
          {/* ─── L'ÉQUIPE : les comptes de cette boutique, dans son schéma. */}
          <ListeComptes
            storeId={storeId}
            comptes={comptes}
            roles={roles}
            onChange={charger}
            onErreur={setErreur}
          />

          {/* ─── Les comptes de la boutique qui vivent dans la table
              COMMUNE : son administrateur au premier chef, créé par la console
              au provisionnement. Ils se lisent ici mais ne s'y gèrent pas.
              Section escamotée si elle est vide — un tableau sans ligne sous un
              titre ressemble à une panne de chargement. */}
          {membresAffiches.length > 0 && (
            <>
              <Typography variant="h3" sx={{ mt: 8, mb: 1 }}>
                Administration
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Administrateurs de la boutique. Leur compte est créé et révoqué depuis la
                console.
              </Typography>
              <ListeMembres membres={membresAffiches} />
            </>
          )}
        </>
      )}

      {tab === 1 && (
        <MatricePermissions
          storeId={storeId}
          roles={roles}
          permissions={permissions}
          onChange={charger}
          onErreur={setErreur}
        />
      )}
    </Box>
  )
}

// ------------------------------------------------------------- les personnes

function ListeMembres({ membres }: { membres: StoreMember[] }): JSX.Element {



  return (
    <>
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Dernière connexion</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {membres.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <VignetteFiche
                      images={m.avatarUrl ? [m.avatarUrl] : []}
                      repli={
                        <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'text.disabled' }}>
                          {m.displayName.slice(0, 2).toUpperCase()}
                        </Typography>
                      }
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {m.displayName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{m.email}</TableCell>
                <TableCell>
                  {m.roleName}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      color={COULEUR_STATUT[m.status]}
                      label={MEMBER_STATUS_LABELS[m.status]}
                    />
                    {/* ⚠️ Un compte DÉSACTIVÉ ne se voyait pas : le membre
                        apparaissait « Actif » ici pendant qu'il ne pouvait plus
                        se connecter du tout. Les deux états sont distincts —
                        l'un vaut pour cette boutique, l'autre pour la
                        plateforme entière — et se corrigent ailleurs. */}
                    {m.accountStatus === 'disabled' && (
                      <Chip size="small" color="error" variant="outlined" label="Compte désactivé" />
                    )}
                    {m.autresBoutiques > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${m.autresBoutiques} autre${m.autresBoutiques > 1 ? 's' : ''} boutique${m.autresBoutiques > 1 ? 's' : ''}`}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {dateCourte(m.lastLoginAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

    </>
  )
}


// --------------------------------------------------------------- la matrice

function MatricePermissions({
  storeId,
  roles,
  permissions,
  onChange,
  onErreur,
}: {
  storeId: string
  roles: RoleWithPermissions[]
  permissions: PermissionEntry[]
  onChange: () => Promise<void>
  onErreur: (m: string) => void
}): JSX.Element {
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '')
  const role = roles.find((r) => r.id === roleId) ?? roles[0]
  const [coches, setCoches] = useState<Set<string>>(new Set(role?.permissionIds ?? []))
  const [enregistre, setEnregistre] = useState(false)

  useEffect(() => {
    setCoches(new Set(role?.permissionIds ?? []))
  }, [role])

  /** Regroupe le catalogue par ressource, dans l'ordre d'affichage voulu. */
  const parRessource = useMemo(() => {
    const map = new Map<string, PermissionEntry[]>()
    for (const p of permissions) {
      const liste = map.get(p.resource) ?? []
      liste.push(p)
      map.set(p.resource, liste)
    }
    const ordonnees = RESOURCE_ORDER.filter((r) => map.has(r))
    const reste = [...map.keys()].filter((r) => !RESOURCE_ORDER.includes(r) && r !== 'platform')
    return [...ordonnees, ...reste].map((r) => ({ resource: r, entries: map.get(r)! }))
  }, [permissions])

  const modifie = useMemo(() => {
    const avant = new Set(role?.permissionIds ?? [])
    if (avant.size !== coches.size) return true
    for (const id of coches) if (!avant.has(id)) return true
    return false
  }, [coches, role])

  function basculer(id: string): void {
    setCoches((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  if (!role) return <Alert severity="info">Aucun rôle configurable.</Alert>

  return (
    <Paper sx={{ p: 6, borderRadius: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
          mb: 4,
        }}
      >
        <Select
          size="small"
          value={role.id}
          onChange={(e) => setRoleId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {roles.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name} ({r.memberCount})
            </MenuItem>
          ))}
        </Select>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!modifie || enregistre}
          onClick={async () => {
            setEnregistre(true)
            try {
              await saveRolePermissions(storeId, role.id, [...coches])
              await onChange()
            } catch (e) {
              onErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
            } finally {
              setEnregistre(false)
            }
          }}
        >
          Enregistrer
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ressource</TableCell>
            {MATRIX_ACTIONS.map((a) => (
              <TableCell key={a} align="center">
                {ACTION_LABELS[a]}
              </TableCell>
            ))}
            <TableCell>Spécifique</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parRessource.map(({ resource, entries }) => {
            const specifiques = entries.filter(
              (e) => !MATRIX_ACTIONS.includes(e.action as (typeof MATRIX_ACTIONS)[number]),
            )
            return (
              <TableRow key={resource} hover>
                <TableCell sx={{ fontWeight: 500 }}>
                  {RESOURCE_LABELS[resource] ?? resource}
                </TableCell>
                {MATRIX_ACTIONS.map((action) => {
                  const entry = entries.find((e) => e.action === action)
                  return (
                    <TableCell key={action} align="center">
                      {entry ? (
                        <Checkbox
                          size="small"
                          checked={coches.has(entry.id)}
                          onChange={() => basculer(entry.id)}
                          inputProps={{
                            'aria-label': `${ACTION_LABELS[action]} — ${
                              RESOURCE_LABELS[resource] ?? resource
                            }`,
                          }}
                        />
                      ) : (
                        // Un tiret, pas une case grisée : l'action n'existe pas
                        // pour cette ressource, elle n'est pas « refusée ».
                        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                  )
                })}
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {specifiques.map((e) => (
                      <Box
                        key={e.id}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        title={e.description ?? e.code}
                      >
                        <Checkbox
                          size="small"
                          checked={coches.has(e.id)}
                          onChange={() => basculer(e.id)}
                          inputProps={{ 'aria-label': e.code }}
                        />
                        <Typography variant="caption">{e.action}</Typography>
                      </Box>
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {role.isSystem && (
        <Alert severity="info" sx={{ mt: 4 }}>
          « {role.name} » est un rôle standard partagé par toutes les boutiques. Vos modifications
          en créeront une copie propre à cette boutique — les autres ne seront pas touchées.
        </Alert>
      )}
    </Paper>
  )
}
